import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { setCurrentSupabaseOrganization } from "@/lib/supabaseOrganization";
import { useCampaigns } from "@/hooks/useCampaigns";

export interface DashboardStats {
  activeCampaigns: number;
  totalInfluencers: number;
  totalReach: number;
  revenue: number;
}

export function useDashboardStats() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { data: campaigns, isLoading: campaignsLoading } = useCampaigns();

  const query = useQuery({
    queryKey: ["dashboard-stats", orgId],
    queryFn: async (): Promise<DashboardStats> => {
      if (!orgId) {
        throw new Error('No current organization selected');
      }

      await setCurrentSupabaseOrganization(orgId);

      // Fetch tenant-scoped influencers count and total reach
      const { data: orgInfluencers, error: infError } = await supabase
        .from('influencers')
        .select('followers_count')
        .eq('organization_id', orgId);

      if (infError) {
        console.error('Error fetching tenant influencers for stats:', infError);
      }

      const tenantInfluencers = orgInfluencers || [];
      const totalInfluencers = tenantInfluencers.length;
      const totalReach = tenantInfluencers.reduce(
        (sum, inf) => sum + (Number(inf.followers_count) || 0),
        0
      );

      // Fetch revenue safely from paid payments for this organization's campaigns
      let revenue = 0;
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_dashboard_stats');
        if (!rpcError && rpcData && typeof rpcData === 'object' && 'revenue' in rpcData) {
          revenue = Number((rpcData as any).revenue) || 0;
        }
      } catch {
        revenue = 0;
      }

      return {
        activeCampaigns: 0,
        totalInfluencers,
        totalReach,
        revenue,
      };
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    const activeCount = campaigns ? campaigns.filter((c) => c.status === 'active').length : 0;
    return {
      ...query.data,
      activeCampaigns: activeCount,
    };
  }, [query.data, campaigns]);

  return {
    ...query,
    data,
    isLoading: query.isLoading || (campaignsLoading && !data),
  };
}
